import React, { useEffect, useState } from 'react';
import {
  Users,
  DollarSign,
  TrendingUp,
  Activity,
  Film,
  CheckCircle2,
  Clock,
  AlertTriangle,
} from 'lucide-react';
import { Header } from '../components/Header.js';
import { EduCard } from '../components/EduCard.js';
import { api } from '../services/api.js';
import { MasterDashboardData } from '../types/index.js';

export const Dashboard: React.FC = () => {
  const [data, setData] = useState<MasterDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const res = await api.getMasterDashboard();
        setData(res);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  return (
    <div>
      <Header
        title="Dashboard Geral Master"
        subtitle="Visão executiva e indicadores consolidados comerciais, financeiros e de renderização"
      />

      <div className="p-8 max-w-7xl mx-auto space-y-8">
        {/* Section 1: Commercial & Financial Metrics */}
        <div>
          <h2 className="text-sm font-bold text-txt-primary uppercase tracking-wider mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-brand-primary" />
            <span>Indicadores Comerciais & Receita SaaS</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">MRR (Recorrência Mensal)</span>
                <span className="p-2 bg-green-50 text-status-success rounded-lg">
                  <DollarSign size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                R$ {(data?.commercial.mrr || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                ARR estimado: R$ {(data?.commercial.arr || 0).toLocaleString('pt-BR')}
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Clientes Cadastrados</span>
                <span className="p-2 bg-brand-light text-brand-primary rounded-lg">
                  <Users size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                {data?.commercial.totalCustomers || 0}
              </span>
              <span className="text-[11px] text-status-success mt-1 block">
                {data?.commercial.activeCustomers || 0} ativos • {data?.commercial.suspendedCustomers || 0} suspensos
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Assinaturas Ativas</span>
                <span className="p-2 bg-blue-50 text-status-info rounded-lg">
                  <TrendingUp size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                {data?.commercial.activeSubscriptions || 0}
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                Planos Starter, Pro e Business
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Ticket Médio</span>
                <span className="p-2 bg-orange-50 text-brand-primary rounded-lg">
                  <DollarSign size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                R$ {data?.commercial.averageTicket || 0}
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                Por cliente ativo / mês
              </span>
            </EduCard>
          </div>
        </div>

        {/* Section 2: Operational & Render Engine Metrics */}
        <div>
          <h2 className="text-sm font-bold text-txt-primary uppercase tracking-wider mb-4 flex items-center gap-2">
            <Activity size={16} className="text-brand-primary" />
            <span>Indicadores Operacionais de Produção Multimídia</span>
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Videoaulas Produzidas</span>
                <span className="p-2 bg-slate-100 text-txt-primary rounded-lg">
                  <Film size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                {data?.operational.totalVideosProduced || 0}
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                Total acumulado de MP4 gerados
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Fila de Renderização</span>
                <span className="p-2 bg-brand-light text-brand-primary rounded-lg">
                  <Activity size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-brand-primary mt-2 block">
                {data?.operational.runningJobsCount || 0}
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                Jobs ativos nos workers Linux
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Taxa de Sucesso</span>
                <span className="p-2 bg-green-50 text-status-success rounded-lg">
                  <CheckCircle2 size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-status-success mt-2 block">
                {data?.operational.successRate || 100}%
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                {data?.operational.failedJobsCount || 0} falhas registradas
              </span>
            </EduCard>

            <EduCard>
              <div className="flex items-center justify-between">
                <span className="text-xs text-txt-secondary">Tempo Médio / Vídeo</span>
                <span className="p-2 bg-slate-100 text-txt-secondary rounded-lg">
                  <Clock size={16} />
                </span>
              </div>
              <span className="text-2xl font-black text-txt-primary mt-2 block">
                {data?.operational.averageRenderSeconds || 42}s
              </span>
              <span className="text-[11px] text-txt-muted mt-1 block">
                Piper TTS + FFmpeg 720p
              </span>
            </EduCard>
          </div>
        </div>

        {/* Server & Node Telemetry Summary Card */}
        <EduCard
          title="Status da Infraestrutura DigitalOcean & Workers"
          subtitle="Monitoramento em tempo real dos serviços systemd e recursos de hardware"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-surface-secondary border border-bdr rounded-xl">
              <span className="text-xs text-txt-secondary block">Processo 1: API Fastify</span>
              <span className="text-sm font-bold text-status-success mt-1 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-status-success"></span>
                Operacional (Porta 3001 / Nginx SSL)
              </span>
              <p className="text-[11px] text-txt-muted mt-2">
                Hospedado no Droplet Ubuntu Server LTS com proxy reverso e proteção CORS estrita.
              </p>
            </div>

            <div className="p-4 bg-surface-secondary border border-bdr rounded-xl">
              <span className="text-xs text-txt-secondary block">Processo 2: Worker Linux</span>
              <span className="text-sm font-bold text-status-success mt-1 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-status-success"></span>
                Ativo (Lease: 120s / Heartbeat: 15s)
              </span>
              <p className="text-[11px] text-txt-muted mt-2">
                FFmpeg 4.4+ e Piper TTS neural com modelo Faber em execução isolada sem privilégios root.
              </p>
            </div>

            <div className="p-4 bg-surface-secondary border border-bdr rounded-xl">
              <span className="text-xs text-txt-secondary block">Camada de Dados: Firebase</span>
              <span className="text-sm font-bold text-status-success mt-1 flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-status-success"></span>
                Conectado (Auth, Firestore, Storage)
              </span>
              <p className="text-[11px] text-txt-muted mt-2">
                Controle transacional de franquias e coordenação de fila sem dependência de Redis.
              </p>
            </div>
          </div>
        </EduCard>
      </div>
    </div>
  );
};
